import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Circus Daily ProgressBar — Figma 10023:34514
 * flat continuous bar or segmented stepper pills.
 */
export type ProgressBarProps = {
  /** 0–1 or 0–100 */
  value: number;
  variant?: 'flat' | 'segmented';
  /** Segment count when variant="segmented" (default 5) */
  segments?: number;
  className?: string;
};

function normalizeValue(value: number): number {
  if (value > 1) return Math.min(1, Math.max(0, value / 100));
  return Math.min(1, Math.max(0, value));
}

const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(
  ({ value, variant = 'flat', segments = 5, className }, ref) => {
    const ratio = normalizeValue(value);

    if (variant === 'segmented') {
      const completed = Math.round(ratio * segments);
      return (
        <div
          ref={ref}
          role="progressbar"
          aria-valuenow={Math.round(ratio * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
          className={cn('flex w-full items-center gap-2', className)}
          data-test-id="progress-bar-segmented"
        >
          {Array.from({ length: segments }, (_, i) => (
            <div
              key={i}
              className={cn(
                'h-1.5 min-w-px flex-1 rounded-full',
                i < completed ? 'bg-white' : 'bg-surface-muted'
              )}
            />
          ))}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        role="progressbar"
        aria-valuenow={Math.round(ratio * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        className={cn('h-1.5 w-full overflow-hidden rounded-full bg-surface-muted', className)}
        data-test-id="progress-bar-flat"
      >
        <div
          className="h-full rounded-full bg-white transition-[width] duration-300"
          style={{ width: `${ratio * 100}%` }}
        />
      </div>
    );
  }
);
ProgressBar.displayName = 'ProgressBar';

export { ProgressBar };
